import { useEffect, useMemo, useState } from "react";
import { resolvePortalServiceSectionStorageKey } from "@/lib/portalShell";

type HealthPeriodPreset = "7d" | "30d" | "90d";

type UseHealthFiltersOptions = {
  defaultServiceKey?: string;
  defaultCareModality?: string;
  defaultPeriodPreset?: HealthPeriodPreset;
  defaultSectionId?: string;
};

type HealthFiltersState = {
  from?: string;
  to?: string;
  serviceKey?: string;
  careModality?: string;
  companyId?: number;
  channelId?: number;
};

function formatDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function buildRange(preset: HealthPeriodPreset) {
  const end = new Date();
  end.setUTCHours(12, 0, 0, 0);

  const start = new Date(end);
  const daysByPreset: Record<HealthPeriodPreset, number> = {
    "7d": 6,
    "30d": 29,
    "90d": 89,
  };

  start.setUTCDate(start.getUTCDate() - daysByPreset[preset]);

  return {
    from: formatDate(start),
    to: formatDate(end),
  };
}

function readStoredSection(serviceKey?: string, fallback = "visao-geral") {
  if (!serviceKey || typeof window === "undefined") {
    return fallback;
  }

  return window.localStorage.getItem(resolvePortalServiceSectionStorageKey(serviceKey)) || fallback;
}

function persistSection(serviceKey: string | undefined, sectionId: string) {
  if (!serviceKey || typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(resolvePortalServiceSectionStorageKey(serviceKey), sectionId);
  window.dispatchEvent(new CustomEvent("pjc:portal-section-change", { detail: { moduleKey: serviceKey, sectionId } }));
}

export function useHealthFilters(options: UseHealthFiltersOptions = {}) {
  const {
    defaultServiceKey,
    defaultCareModality,
    defaultPeriodPreset = "30d",
    defaultSectionId = "visao-geral",
  } = options;

  const [periodPreset, setPeriodPreset] = useState<HealthPeriodPreset>(defaultPeriodPreset);
  const [careModality, setCareModality] = useState<string | undefined>(defaultCareModality);
  const [companyId, setCompanyId] = useState<number | undefined>(undefined);
  const [channelId, setChannelId] = useState<number | undefined>(undefined);
  const [activeSectionId, setActiveSectionIdState] = useState<string>(() => readStoredSection(defaultServiceKey, defaultSectionId));

  const range = useMemo(() => buildRange(periodPreset), [periodPreset]);

  const filters = useMemo<HealthFiltersState>(
    () => ({
      from: range.from,
      to: range.to,
      serviceKey: defaultServiceKey,
      careModality,
      companyId,
      channelId,
    }),
    [range.from, range.to, defaultServiceKey, careModality, companyId, channelId],
  );

  useEffect(() => {
    setActiveSectionIdState(readStoredSection(defaultServiceKey, defaultSectionId));
  }, [defaultServiceKey, defaultSectionId]);

  useEffect(() => {
    const handleSectionChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ moduleKey?: string; sectionId?: string }>;
      if (customEvent.detail?.moduleKey && customEvent.detail.moduleKey !== defaultServiceKey) {
        return;
      }

      if (customEvent.detail?.sectionId) {
        setActiveSectionIdState(customEvent.detail.sectionId);
        return;
      }

      setActiveSectionIdState(readStoredSection(defaultServiceKey, defaultSectionId));
    };

    window.addEventListener("pjc:portal-section-change", handleSectionChange as EventListener);
    return () => window.removeEventListener("pjc:portal-section-change", handleSectionChange as EventListener);
  }, [defaultServiceKey, defaultSectionId]);

  const setActiveSectionId = (nextSectionId: string) => {
    setActiveSectionIdState(nextSectionId);
    persistSection(defaultServiceKey, nextSectionId);
  };

  const resetFilters = () => {
    setPeriodPreset(defaultPeriodPreset);
    setCareModality(defaultCareModality);
    setCompanyId(undefined);
    setChannelId(undefined);
    setActiveSectionId(defaultSectionId);
  };

  return {
    filters,
    periodPreset,
    setPeriodPreset,
    careModality,
    setCareModality,
    companyId,
    setCompanyId,
    channelId,
    setChannelId,
    activeSectionId,
    setActiveSectionId,
    resetFilters,
  };
}

export type { HealthFiltersState, HealthPeriodPreset };
